import { Controller } from '@hotwired/stimulus';

const DEBOUNCE_DELAY = 500; // 500ms debounce for layout changes

export default class extends Controller {
  static values = {
    url: String,
    templateId: String
  }

  static targets = ['saveIndicator']

  connect() {
    console.log('💾 LayoutAutosave Controller Connected!');
    console.log('📊 Values:', {
      url: this.urlValue,
      templateId: this.templateIdValue
    });
    console.log('🎪 Targets found:', {
      saveIndicator: this.hasSaveIndicatorTarget
    });

    this.debounceTimer = null;
    this.isLoading = false;
    this.pendingSaves = new Map(); // Track pending saves by field ID
    this.debugMode = true;
  }

  disconnect() {
    this.clearTimer();
  }

  // Main method called when a field is moved
  saveFieldPosition(fieldId, layoutData) {
    if (this.debugMode) {
      console.log('💾 Auto-save triggered for field:', fieldId, layoutData);
    }

    this.clearTimer();

    // Don't auto-save during submission
    if (this.isLoading) {
      console.log('⏳ Auto-save skipped - already loading');
      return;
    }

    // Store the latest position data for this field
    this.pendingSaves.set(fieldId, layoutData);
    console.log('📦 Pending saves:', this.pendingSaves.size);

    this.debounceTimer = setTimeout(() => {
      this.performBatchSave();
    }, DEBOUNCE_DELAY);
  }

  clearTimer() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  async performBatchSave() {
    if (this.pendingSaves.size === 0) {
      console.log('📭 No pending saves to process');
      return;
    }

    console.log(`🚀 Starting batch save for ${this.pendingSaves.size} fields`);
    this.isLoading = true;
    this.showSavingState();

    // Process all pending saves
    const savePromises = Array.from(this.pendingSaves.entries()).map(([fieldId, layoutData]) => {
      console.log(`💾 Saving field ${fieldId}:`, layoutData);
      return this.saveIndividualField(fieldId, layoutData);
    });

    try {
      const results = await Promise.all(savePromises);
      console.log('✅ All saves completed successfully:', results.length);
      this.showSavedState();
      this.pendingSaves.clear();
    } catch (error) {
      console.error('❌ Layout auto-save error:', error);
      this.showErrorState();
    } finally {
      this.isLoading = false;
      this.hideSavingStateAfterDelay();
    }
  }

  async saveIndividualField(fieldId, layoutData) {
    const formData = new FormData();
    formData.append('field_id', fieldId);

    // Add layout data
    if (layoutData.layoutRow !== undefined) {
      formData.append('layout_row', layoutData.layoutRow);
    }
    if (layoutData.layoutCol !== undefined) {
      formData.append('layout_col', layoutData.layoutCol);
    }
    if (layoutData.widthRatio !== undefined) {
      formData.append('width_ratio', layoutData.widthRatio);
    }
    if (layoutData.group !== undefined) {
      formData.append('group', layoutData.group || '');
    }
    if (layoutData.position !== undefined) {
      formData.append('position', layoutData.position);
    }

    formData.append('authenticity_token', this.getAuthenticityToken());

    const response = await fetch(this.urlValue, {
      method: 'PATCH',
      body: formData,
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response;
  }

  showSavingState() {
    this.updateSaveIndicator('💾 Saving layout...', 'text-muted');
  }

  showSavedState() {
    this.updateSaveIndicator('✓ Auto-saved', 'text-success');
  }

  showErrorState() {
    this.updateSaveIndicator('❌ Save failed', 'text-danger');
  }

  updateSaveIndicator(message, cssClass) {
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.textContent = message;
      this.saveIndicatorTarget.className = `auto-save-status ${cssClass}`;
    } else {
      // Fallback: create indicator in toolbar if target doesn't exist
      const toolbar = document.querySelector('.layout-builder-toolbar');
      if (toolbar) {
        let indicator = toolbar.querySelector('.layout-save-indicator');
        if (!indicator) {
          indicator = document.createElement('span');
          indicator.className = 'layout-save-indicator ms-3';
          indicator.id = 'layout_save_indicator';
          toolbar.querySelector('.toolbar-right').appendChild(indicator);
        }
        indicator.textContent = message;
        indicator.className = `layout-save-indicator ms-3 ${cssClass}`;
      }
    }
  }

  hideSavingStateAfterDelay() {
    setTimeout(() => {
      if (this.hasSaveIndicatorTarget) {
        this.saveIndicatorTarget.textContent = '';
        this.saveIndicatorTarget.className = 'auto-save-status';
      } else {
        const indicator = document.querySelector('.layout-save-indicator');
        if (indicator) {
          indicator.textContent = '';
          indicator.className = 'layout-save-indicator ms-3';
        }
      }
    }, 3000); // Remove indicator after 3 seconds
  }

  getAuthenticityToken() {
    const tokenElement = document.querySelector('meta[name="csrf-token"]');
    return tokenElement ? tokenElement.content : '';
  }

  // Helper method for external controllers to trigger auto-save
  triggerAutoSave(fieldId, layoutData) {
    console.log('💾 Layout autosave controller called with:', fieldId, layoutData);
    this.saveFieldPosition(fieldId, layoutData);
  }

  // Helper method for section header position updates
  saveSectionHeaderPosition(sectionId, layoutData) {
    console.log('💾 Section header autosave called with:', sectionId, layoutData);
    this.saveSectionHeaderPositionDirect(sectionId, layoutData);
  }

  async saveSectionHeaderPositionDirect(sectionId, layoutData) {
    console.log('🔄 Saving section header position directly:', sectionId, layoutData);

    const formData = new FormData();
    formData.append('section_id', sectionId);

    if (layoutData.position !== undefined) {
      formData.append('position', layoutData.position);
    }

    formData.append('authenticity_token', this.getAuthenticityToken());

    try {
      const response = await fetch(`/event_doc_templates/${this.templateIdValue}/fields/save_section_header`, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Section header position updated successfully:', result);
      return response;
    } catch (error) {
      console.error('❌ Error saving section header position:', error);
      throw error;
    }
  }
}